/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import Debounce from 'lodash/debounce' // Npm: lodash library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from "components/DownloadToExcel";
import Modal from 'components/Modal'
import PaymentUpsert from 'components/PaymentUpsert'
import PaymentDelete from 'components/PaymentDelete'
import PaymentDropDownOptions from 'components/PaymentDropDownOptions'


/*
 * STYLES
 */
import { buttonStyle } from "./index.style";


/*
 * GRAPHS
 */
import PaymentReadQuery from './__query__/index.payment.read.query'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [searchQuery, setSearchQuery] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const {
    onOpen: onPaymentCreateOpen,
    isOpen: isPaymentCreateOpen,
    onClose: onPaymentCreateClose,
  } = useDisclosure();
  const {
    onOpen: onPaymentUpdateOpen,
    isOpen: isPaymentUpdateOpen,
    onClose: onPaymentUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onPaymentDeleteOpen,
    isOpen: isPaymentDeleteOpen,
    onClose: onPaymentDeleteClose,
  } = useDisclosure();
  const _QueryPaymentRead = useQuery(PaymentReadQuery, {
    variables: {
      vendorId: passOn?.vendorId,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
      search: searchQuery,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
  });
  const { refetch: _RefetchPaymentRead } = _QueryPaymentRead;
  const _onSearchInputChange = React.useCallback(
    Debounce((e) => {
      setPaginationModel((j) => ({ ...j, page: 0 }));
      setSearchQuery(e.target.value);
    }, 800),
    [],
  );
  const _tableHeaderHeightRef = React.useRef(0);
  const _rowCountRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _RefetchPaymentRead();
    } finally {
      setIsRefreshing(false);
    }
  }, [_RefetchPaymentRead]);

  React.useEffect(() => {
    return () => _onSearchInputChange.cancel?.();
  }, [_onSearchInputChange]);

  // Data assignment.
  const _QueryPaymentReadRowCount = _.pluck(
    _QueryPaymentRead?.data?.PaymentRead,
    "_totalCount",
  )?.[0];
  const rowCount = React.useMemo(() => {
    if (
      undefined !== _QueryPaymentReadRowCount &&
      null !== _QueryPaymentReadRowCount
    )
      _rowCountRef.current = _QueryPaymentReadRowCount;

    return _rowCountRef.current;
  }, [_QueryPaymentReadRowCount]);
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryPaymentRead?.data?.PaymentRead?.map((item, index) =>
          _successFlags.includes(item.status)
            ? {
                id:
                  item?.id ??
                  `vendor-payment-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.pageSize * paginationModel.page + 1}.`,
                amount: item?.amount ?? "-",
                createdAt: Moment(item?.createdAt).format("DD-MM-YYYY"),
                updatedAt: Moment(item?.updatedAt).format("DD-MM-YYYY"),
              }
            : null,
        ),
      ),
    [
      _QueryPaymentRead?.data?.PaymentRead,
      _successFlags,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 90,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: "amount",
        headerName: `Amount ( in ${passOn?.currency ?? "EUR"} )`,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <PaymentDropDownOptions
            type={passOn?.type}
            paymentId={row?.id}
            onPaymentUpdate={onPaymentUpdateOpen}
            onPaymentDelete={onPaymentDeleteOpen}
          />
        ),
      },
    ],
    [onPaymentDeleteOpen, onPaymentUpdateOpen, passOn?.currency, passOn?.type],
  );

  // Return component.
  return (
    <>
      <Flex className="payment base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          height="calc(100vh - 120px)"
          minH={0}
          maxH="calc(100vh - 120px)"
          overflow="hidden"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Create new payment." fontSize="sm">
              <Button
                onClick={onPaymentCreateOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                Create Payment
              </Button>
            </Tooltip>
            <Flex gap="12px" flexWrap="wrap">
              <Button
                borderRadius="8px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_QueryPaymentRead?.data?.PaymentRead?.map(
                  (item, __index) =>
                    _successFlags.includes(item.status)
                      ? {
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          [`Amount ( in ${passOn?.currency ?? "EUR"} )`]:
                            item?.amount ?? "-",
                          CreatedAt: Moment(item?.createdAt).format(
                            "DD-MM-YYYY",
                          ),
                          UpdatedAt: Moment(item?.updatedAt).format(
                            "DD-MM-YYYY",
                          ),
                        }
                      : {},
                )}
                headersData={[
                  "S.No.",
                  `Amount ( in ${passOn?.currency ?? "EUR"} )`,
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            loading={_QueryPaymentRead?.loading}
            sx={{ height: "100%" }}
          />
        </Flex>
        <Modal
          size="md"
          title="Add New Payment"
          isOpen={isPaymentCreateOpen}
          onClose={onPaymentCreateClose}
        >
          <PaymentUpsert isCreateOnly={true} isVendorOnly />
        </Modal>
        <Modal
          size="md"
          title="Update Payment"
          isOpen={isPaymentUpdateOpen}
          onClose={onPaymentUpdateClose}
        >
          <PaymentUpsert isVendorOnly />
        </Modal>
        <Modal
          size="md"
          title="Delete Payment"
          isOpen={isPaymentDeleteOpen}
          onClose={onPaymentDeleteClose}
        >
          <PaymentDelete />
        </Modal>
      </Flex>
    </>
  );
};


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
